import { useTranslate } from '@tolgee/react'
import { isURL } from 'validator'
import { SERVICE_TYPES } from '../_utils/constants'
import { isVPCMode } from '@/lib/utils'

export interface ServiceRegistryFormData {
  serviceType: string
  name: string
  url: string
  key: string
}

export type ServiceRegistryFormValidationErrors = Partial<ServiceRegistryFormData> & {
  isConnected?: string
}

export function useServiceRegistryFormValidation({
  isOAuthConnected = false,
}: {
  isOAuthConnected: boolean
}) {
  const { t } = useTranslate()

  const validateServiceName = (serviceName?: string): string | undefined => {
    const trimmedName = serviceName?.trim()
    if (!trimmedName) {
      return t('serviceRegistry.dialog.validation.nameRequired')
    }
    return undefined
  }

  const validateServiceUrl = (serviceUrl?: string): string | undefined => {
    const trimmedUrl = serviceUrl?.trim()
    if (!trimmedUrl) {
      return t('serviceRegistry.dialog.validation.urlRequired')
    }
    if (!isValidUrl(trimmedUrl)) {
      return t('serviceRegistry.dialog.validation.urlInvalid')
    }
    return undefined
  }

  const validateApiKey = (apiKey?: string): string | undefined => {
    const trimmedApiKey = apiKey?.trim()
    if (!trimmedApiKey) {
      return t('serviceRegistry.dialog.validation.apiKeyRequired')
    }
    return undefined
  }

  const isValidUrl = (url: string): boolean => {
    return isURL(url, {
      protocols: isVPCMode ? ['http', 'https'] : ['https'],
      require_protocol: true,
      require_tld: !isVPCMode,
    })
  }

  const canOAuthInitConnection = ({
    formData,
  }: {
    formData?: Partial<ServiceRegistryFormData>
  }): boolean => {
    return !!(
      !validateServiceName(formData?.name) &&
      !validateServiceUrl(formData?.url) &&
      formData?.serviceType === SERVICE_TYPES.MCP_OAUTH2 &&
      !isOAuthConnected
    )
  }

  const validateDialogForm = (
    formData: Partial<ServiceRegistryFormData>,
  ): ServiceRegistryFormValidationErrors => {
    const errors: ServiceRegistryFormValidationErrors = {}
    const { name, key, url, serviceType } = formData

    const serviceNameError = validateServiceName(name)
    if (serviceNameError) {
      errors.name = serviceNameError
    }

    const serviceUrlError = validateServiceUrl(url)
    if (serviceUrlError) {
      errors.url = serviceUrlError
    }

    if (serviceType === SERVICE_TYPES.MCP_KEY) {
      const apiKeyError = validateApiKey(key)
      if (apiKeyError) {
        errors.key = apiKeyError
      }
    }

    if (serviceType === SERVICE_TYPES.MCP_OAUTH2 && !isOAuthConnected) {
      errors.isConnected = t('serviceRegistry.dialog.validation.oauthConnectionRequired')
    }
    return errors
  }

  return { validateDialogForm, canOAuthInitConnection }
}
